import apiClient from './api'; // API client for HTTP requests

// ==============================
// Recommendation Functions & Types
// ==============================

export interface RecommendationPayload {
  applicationId: string;
  teacherId: string;
  content: string;
}

export interface RecommendationUpdatePayload {
  content: string;
}

/**
 * Create a new recommendation.
 */
export const createRecommendation = async (data: RecommendationPayload) => {
  try {
    const response = await apiClient.post('/crowdfunding/recommendations', data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Get a recommendation by ID.
 */
export const getRecommendationById = async (id: string) => {
  try {
    const response = await apiClient.get(`/crowdfunding/recommendations/${id}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Get all recommendations for a specific application.
 */
export const getRecommendationsByApplication = async (applicationId: string) => {
  try {
    const response = await apiClient.get(`/crowdfunding/recommendations/application/${applicationId}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Get all recommendations submitted by a specific teacher.
 */
export const getRecommendationsByTeacher = async (teacherId: string) => {
  try {
    const response = await apiClient.get(`/crowdfunding/recommendations/teacher/${teacherId}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Update an existing recommendation.
 */
export const updateRecommendation = async (id: string, updates: RecommendationUpdatePayload) => {
  try {
    const response = await apiClient.put(`/crowdfunding/recommendations/${id}`, updates);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Delete a recommendation.
 */
export const deleteRecommendation = async (id: string) => {
  try {
    const response = await apiClient.delete(`/crowdfunding/recommendations/${id}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// ==============================
// Criteria Functions & Types
// ==============================

export interface CriteriaScorePayload {
  applicationId: string;
  scores: object; // Example: { criterion1: score1, criterion2: score2 }
}

/**
 * Fetch all criteria for application review.
 */
export const getCriteria = async () => {
  try {
    const response = await apiClient.get('/crowdfunding/criteria');
    return response.data; // Criteria list
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Submit or update criteria scores for an application.
 */
export const submitCriteriaScores = async (data: CriteriaScorePayload) => {
  try {
    const response = await apiClient.post('/crowdfunding/criteria', data);
    return response.data; // Updated application scores or confirmation
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// ==============================
// Document Upload Functions & Types
// ==============================

export interface DocumentUploadPayload {
  applicationId: string;
  document: File; // File to upload
}

/**
 * Upload a document for an application.
 */
export const uploadDocument = async (data: DocumentUploadPayload) => {
  try {
    const formData = new FormData();
    formData.append('applicationId', data.applicationId);
    formData.append('document', data.document);

    const response = await apiClient.post('/crowdfunding/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data', // Required for file uploads
      },
    });
    return response.data; // Confirmation or uploaded document details
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetch all documents for a specific application.
 */
export const getDocumentsByApplication = async (applicationId: string) => {
  try {
    const response = await apiClient.get(`/crowdfunding/documents?applicationId=${applicationId}`);
    return response.data; // List of documents
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// ==============================
// Application Functions & Types
// ==============================

export interface CreateApplicationPayload {
  applicantId: string;
  criteria: object;
}

export interface UpdateApplicationPayload {
  status?: string;
  criteriaScores?: object;
}

/**
 * Create a new application.
 */
export const createApplication = async (data: CreateApplicationPayload) => {
  try {
    const response = await apiClient.post('/crowdfunding/applications', data);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetch all applications.
 */
export const getAllApplications = async () => {
  try {
    const response = await apiClient.get('/crowdfunding/applications');
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetch a specific application by ID.
 */
export const getApplicationById = async (id: string) => {
  try {
    const response = await apiClient.get(`/crowdfunding/applications/${id}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Update the status or criteria scores of an application.
 */
export const updateApplication = async (id: string, updates: UpdateApplicationPayload) => {
  try {
    const response = await apiClient.put(`/crowdfunding/applications/${id}`, updates);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Delete an application.
 */
export const deleteApplication = async (id: string) => {
  try {
    const response = await apiClient.delete(`/crowdfunding/applications/${id}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// ==============================
// Parental Consent Function & Types
// ==============================

export interface ParentalConsentPayload {
  parentName: string;
  consent: boolean;
  applicationId: string; // Reference to the application
}

/**
 * Submit parental consent for an application.
 */
export const submitParentalConsent = async (payload: ParentalConsentPayload) => {
  try {
    const response = await apiClient.post("/crowdfunding/parental-consent", payload);
    return response.data;
  } catch (error) {
    console.error("Error submitting parental consent:", error);
    throw error;
  }
};

// ==============================
// Utility: Centralized Error Handling
// ==============================

const handleApiError = (error: unknown) => {
  if (error instanceof Error) {
    console.error('Error:', error.message);
  } else {
    console.error('Unexpected error:', error);
  }
};
